import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useChatInitialization } from './hooks/useChatInitialization';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { useSignalRConnection } from './hooks/useSignalRConnection';
import { useMessageHandling } from './hooks/useMessageHandling';
import { useChatLanguage } from './hooks/useChatLanguage';
import { useChatHistoryRecovery } from './hooks/useChatQuery';
import { ChatInputRef, CustomerVerificationCard } from './Components';
import { Message } from './models';
import { DraftsByTurn } from './services/drafts';
import { getChatStrings } from './i18n';
import { ChatScreen } from './ChatScreen';
import { PreviewChat, usePreviewScenario } from './dev/PreviewChat';

/**
 * The chat page: wires the screen to the live session.
 *
 * - Chat initialization and authentication
 * - SignalR connection for real-time messaging (with reconnect recovery) and answer drafts
 * - Message handling (idempotent turns, retry, soft timeout)
 * - UI language / language hint
 * - User verification flow
 */
export function ChatPage() {
   const preview = usePreviewScenario();
   if (preview) return <PreviewChat scenario={preview} />;
   return <LiveChat />;
}

function LiveChat() {
   const [messages, setMessages] = useState<Message[]>([]);
   // The answers still being written, by turn: beside the message list, never in it
   const [drafts, setDrafts] = useState<DraftsByTurn>({});
   const chatInputRef = useRef<ChatInputRef>(null);

   // The strings follow the UI language, which the initialization hook needs before it knows the language:
   // notices read them through a ref at the moment they are raised.
   const stringsRef = useRef(getChatStrings('en'));
   const getStrings = useCallback(() => stringsRef.current, []);

   // Initialize chat session and handle authentication
   const { canTypeMessages, preferredLanguage, hotelName, suggestedQuestions } = useChatInitialization({ setMessages, getStrings });

   // Keep an active guest's session alive; an expired one goes back to sign-in
   useTokenRefresh();

   // UI language (EN/EL) and the language hint sent with messages
   const { uiLanguage, setLanguage, getLanguageHint } = useChatLanguage(preferredLanguage);
   const strings = getChatStrings(uiLanguage);
   stringsRef.current = strings;

   // The document title names the hotel once the API sends its name.
   useEffect(() => {
      document.title = hotelName ? `${hotelName} · ${strings.conciergeTitle}` : `${strings.conciergeTitle} · XenioAI`;
   }, [hotelName, strings.conciergeTitle]);

   // Manage SignalR connection for real-time messages; refetch history after gaps
   const recoverHistory = useChatHistoryRecovery(canTypeMessages);
   const { isConnected, isConnecting } = useSignalRConnection({ setMessages, setDrafts, recoverHistory });

   // Handle message sending and verification
   const {
      input,
      setInput,
      isBotTyping,
      isBotWriting,
      awaitingTurns,
      visibleDrafts,
      handleSend,
      handleSendText,
      retryMessage,
      showVerification,
      setShowVerification,
      handleCustomerVerification
   } = useMessageHandling({
      messages,
      setMessages,
      drafts,
      setDrafts,
      isConnected,
      uiLanguage,
      getLanguageHint
   });

   // An example question is sent as if typed: the composer takes it, then the next render sends it.
   const pendingSuggestion = useRef<string | null>(null);
   const onSuggestion = useCallback(
      (question: string) => {
         pendingSuggestion.current = question;
         setInput(question);
      },
      [setInput]
   );
   useEffect(() => {
      if (pendingSuggestion.current && input === pendingSuggestion.current) {
         pendingSuggestion.current = null;
         handleSend();
      }
   }, [input, handleSend]);

   return (
      <ChatScreen
         ref={chatInputRef}
         messages={messages}
         isBotTyping={isBotTyping}
         isBotWriting={isBotWriting}
         awaitingTurns={awaitingTurns}
         drafts={visibleDrafts}
         strings={strings}
         language={uiLanguage}
         onLanguageChange={setLanguage}
         hotelName={hotelName}
         suggestedQuestions={suggestedQuestions}
         canTypeMessages={canTypeMessages}
         isConnected={isConnected}
         isConnecting={isConnecting}
         input={input}
         setInput={setInput}
         onSend={handleSend}
         onSendText={handleSendText}
         onRetry={retryMessage}
         onSuggestion={onSuggestion}
      >
         {showVerification && (
            <CustomerVerificationCard onSubmit={handleCustomerVerification} isLoading={isBotTyping} onClose={() => setShowVerification(false)} />
         )}
      </ChatScreen>
   );
}
